import { useCallback } from 'react'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useTracker from 'Pods/Shared/Hooks/useTracker'

const PAGE_TYPE = 'subscription_settings'

const useSubscriptionSettingsTracking = () => {
  const { trackEvent } = useTracker()

  const trackSectionView = useCallback(() => {
    trackEvent(SubscriptionEvents.SETTINGS_VIEWED, { page_type: PAGE_TYPE })
  }, [trackEvent])

  const trackCancelClick = useCallback(() => {
    trackEvent(SubscriptionEvents.CANCEL_CLICK, { page_type: PAGE_TYPE })
  }, [trackEvent])

  const trackCancellationConfirmed = useCallback((reason, subscriptionId, customReason) => {
    const payload = {
      page_type: PAGE_TYPE,
      reason,
      subscription_id: subscriptionId,
    }

    if (customReason) {
      payload.custom_reason = customReason
    }

    trackEvent(SubscriptionEvents.CANCELLATION_CONFIRMED, payload)
  }, [trackEvent])

  const trackRenewClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RENEW_CLICK, { page_type: PAGE_TYPE, subscription_id: subscriptionId })
  }, [trackEvent])

  const trackRenewSuccess = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RENEW_SUCCESS, { page_type: PAGE_TYPE, subscription_id: subscriptionId })
  }, [trackEvent])

  const trackRenewFailure = useCallback((subscriptionId, errorMessage) => {
    trackEvent(SubscriptionEvents.RENEW_FAILURE, {
      page_type: PAGE_TYPE,
      subscription_id: subscriptionId,
      error_message: errorMessage,
    })
  }, [trackEvent])

  const trackReactivateClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.REACTIVATE_CLICK, { page_type: PAGE_TYPE, subscription_id: subscriptionId })
  }, [trackEvent])

  const trackReactivateSuccess = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.REACTIVATE_SUCCESS, { page_type: PAGE_TYPE, subscription_id: subscriptionId })
  }, [trackEvent])

  const trackReactivateFailure = useCallback((subscriptionId, errorMessage) => {
    trackEvent(SubscriptionEvents.REACTIVATE_FAILURE, {
      page_type: PAGE_TYPE,
      subscription_id: subscriptionId,
      error_message: errorMessage,
    })
  }, [trackEvent])

  const trackRetryClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RETRY_PAYMENT_CLICK, {
      page_type: PAGE_TYPE,
      subscription_id: subscriptionId,
    })
  }, [trackEvent])

  const trackPaymentHistoryViewed = useCallback(() => {
    trackEvent(SubscriptionEvents.PAYMENT_HISTORY_VIEWED, { page_type: PAGE_TYPE })
  }, [trackEvent])

  const trackInvoiceDownloadClick = useCallback((subscriptionId, paymentId) => {
    trackEvent(SubscriptionEvents.INVOICE_DOWNLOAD_CLICK, {
      page_type: PAGE_TYPE,
      subscription_id: subscriptionId,
      payment_id: paymentId,
    })
  }, [trackEvent])

  const trackInvoiceDownloadFailed = useCallback((subscriptionId, paymentId, errorMessage) => {
    trackEvent(SubscriptionEvents.INVOICE_DOWNLOAD_FAILED, {
      page_type: PAGE_TYPE,
      subscription_id: subscriptionId,
      payment_id: paymentId,
      error_message: errorMessage,
    })
  }, [trackEvent])

  return {
    trackSectionView,
    trackCancelClick,
    trackCancellationConfirmed,
    trackRenewClick,
    trackRenewSuccess,
    trackRenewFailure,
    trackReactivateClick,
    trackReactivateSuccess,
    trackReactivateFailure,
    trackRetryClick,
    trackPaymentHistoryViewed,
    trackInvoiceDownloadClick,
    trackInvoiceDownloadFailed,
  }
}

export default useSubscriptionSettingsTracking
